import { useState, useRef, useEffect } from 'react';
import {
  EmailShareButton,
  FacebookShareButton,
  LinkedinShareButton,
  EmailIcon,
  FacebookIcon,
  LinkedinIcon,
  PinterestIcon,
} from "react-share";
import { FaInstagram } from 'react-icons/fa';
import { trackButtonClick } from '@/utils/analytics';
import { buildAnalyticsLabel } from './heroUtils';

import saveIcon from '../../../../../public/assets/images/pdp/save.svg';
import savedIcon from '../../../../../public/assets/images/pdp/saved.svg';
import shareIcon from '../../../../../public/assets/images/pdp/share.png';
import printIcon from '../../../../../public/assets/images/pdp/print.png';

/* eslint-disable react/prop-types */
/**
 * HeroActionButtons - Share, Save, Print action buttons for the Commercial PDP Hero Section.
 *
 * Share button opens a dropdown with Email, Facebook, LinkedIn, Pinterest options.
 * Save button toggles filled/outline heart icon based on isSaved prop.
 * Print button calls onPrint prop.
 * All interactions fire trackButtonClick analytics events.
 */
const HeroActionButtons = ({
  propertyId,
  listingId,
  // isUserLoggedIn - auth check handled by parent via onSave callback
  isSaved,
  address,
  onShare,
  onSave,
  onPrint,
}) => {
  const [showShareDropdown, setShowShareDropdown] = useState(false);
  const dropdownRef = useRef(null);

  const currentPageUrl = typeof window !== 'undefined' ? window.location.href : '';

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowShareDropdown(false);
      }
    };

    if (showShareDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showShareDropdown]);

  const fireAnalytics = (action, platform) => {
    const eventLabel = buildAnalyticsLabel(propertyId, listingId, address);
    if (!eventLabel) return;

    trackButtonClick({
      eventName: 'hero_action_click',
      eventCategory: 'COMMERCIAL-PDP',
      eventSource: `${action}${platform ? ` - ${platform}` : ''} Click`,
      eventLabel,
      eventParams: {
        action,
        ...(platform ? { platform } : {}),
      },
    });
  };

  const handleShareClick = () => {
    setShowShareDropdown((prev) => !prev);
    fireAnalytics('share');
    if (onShare) onShare();
  };

  const handleSaveClick = () => {
    fireAnalytics('save');
    if (onSave) onSave();
  };

  const handlePrintClick = () => {
    fireAnalytics('print');
    if (onPrint) onPrint();
  };

  const handleShareOptionClick = (platform) => {
    fireAnalytics('share', platform);
    setShowShareDropdown(false);
  };

  return (
    <div className="commercial-hero__actions" ref={dropdownRef}>
      {/* Save Button */}
      <button
        className={`commercial-hero__actions-button${isSaved ? ' commercial-hero__actions-button--saved' : ''}`}
        onClick={handleSaveClick}
        aria-label={isSaved ? 'Remove from saved properties' : 'Save property'}
      >
        <img src={isSaved ? savedIcon : saveIcon} className="commercial-hero__actions-button-icon" alt="" />
        <span>Save</span>
      </button>

      {/* Share Button + Dropdown Wrapper */}
      <div className="commercial-hero__share-wrapper">
        <button
          className="commercial-hero__actions-button"
          onClick={handleShareClick}
          aria-label="Share property"
          aria-expanded={showShareDropdown}
        >
          <img src={shareIcon} className="commercial-hero__actions-button-icon" alt="" />
          <span>Share</span>
        </button>

        {/* Share Dropdown */}
        {showShareDropdown && (
          <div className="commercial-hero__actions-dropdown">
          <div className="commercial-hero__actions-dropdown-item">
            <EmailShareButton
              body={`Hi,\n\nI found this property you might be interested in on Hubzu.com`}
              subject={`Hubzu.com ${address || ''}`}
              url={currentPageUrl}
              aria-label="Email"
            >
              <EmailIcon size={32} round={true} />
            </EmailShareButton>
          </div>
          <div className="commercial-hero__actions-dropdown-item">
            <FacebookShareButton url={currentPageUrl} aria-label="Facebook">
              <FacebookIcon size={32} round={true} />
            </FacebookShareButton>
          </div>
          <div
            className="commercial-hero__actions-dropdown-item"
            aria-label="Instagram"
            onClick={() => {
              handleShareOptionClick('Instagram');
              window.open('https://www.instagram.com/hubzu_official', '_blank', 'left=20,top=20,width=600,height=500,toolbar=1,resizable=0');
            }}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                handleShareOptionClick('Instagram');
                window.open('https://www.instagram.com/hubzu_official', '_blank', 'left=20,top=20,width=600,height=500,toolbar=1,resizable=0');
              }
            }}
            style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', width: '32px', height: '32px', borderRadius: '50%', background: 'linear-gradient(45deg, #f09433 0%, #e6683c 25%, #dc2743 50%, #cc2366 75%, #bc1888 100%)' }}
          >
            <FaInstagram size={18} color="white" />
          </div>
          <div className="commercial-hero__actions-dropdown-item">
            <LinkedinShareButton url={currentPageUrl} aria-label="LinkedIn">
              <LinkedinIcon size={32} round={true} />
            </LinkedinShareButton>
          </div>
          <div
            className="commercial-hero__actions-dropdown-item"
            aria-label="Pinterest"
            onClick={() => {
              handleShareOptionClick('Pinterest');
              window.open(`//www.pinterest.com/pinthis/?url=${currentPageUrl}`, 'mywin', 'left=20,top=20,width=600,height=500,toolbar=1,resizable=0');
            }}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                handleShareOptionClick('Pinterest');
                window.open(`//www.pinterest.com/pinthis/?url=${currentPageUrl}`, 'mywin', 'left=20,top=20,width=600,height=500,toolbar=1,resizable=0');
              }
            }}
          >
            <PinterestIcon size={32} round={true} />
          </div>
        </div>
      )}
      </div>

      {/* Print Button */}
      <button
        className="commercial-hero__actions-button"
        onClick={handlePrintClick}
        aria-label="Print property details"
      >
        <img src={printIcon} className="commercial-hero__actions-button-icon" alt="" />
        <span>Print</span>
      </button>
    </div>
  );
};

export default HeroActionButtons;
